import { TemplateFormatterEnum } from "../enum/TemplateFormatters.enum";
import { ITemplateExtendedContext } from "../interfaces/template/templateContext.interface";
import { $t } from "./t";
import Vue from "vue";

const Mustache = (() => require("mustache"))();

/**
 * Extracts all variable token paths from a Mustache template string.
 * Returns deduplicated dot-notation paths (e.g. ["report.damageType", "customer.name"]).
 */
export function extractTokensFromTemplate(...templates: string[]): string[] {
  type MustacheNode = [string, string, number, number, MustacheNode[]?];

  function collectTokens(nodes: MustacheNode[], tokens: Set<string>) {
    for (const node of nodes) {
      if (node[0] === "name" || node[0] === "&" || node[0] === "#" || node[0] === "^") {
        tokens.add(node[1]);
        // node[4] contains child nodes for sections
        if (node[4]) {
          collectTokens(node[4], tokens);
        }
      }
    }
  }

  const tokens = new Set<string>();
  for (const template of templates) {
    try {
      const parsed = Mustache.parse(template) as MustacheNode[];
      collectTokens(parsed, tokens);
    } catch (e) {
      Vue.$log.error("extractTokensFromTemplate: malformed template", e);
    }
  }
  return Array.from(tokens);
}

export function renderTemplate(template: string, input: ITemplateExtendedContext): string {
  return Mustache.render(template, {
    ...input,
    /**
     * Expects a json object
     * @example { "key": "{{report.damageType}}", "path": "enums.DamageTypeEnum" }
     * @example enums.DamageTypeEnum.{{report.damageType}}
     * @returns Translated enum value
     */
    [TemplateFormatterEnum.I18N]: function () {
      return (text: string) => {
        try {
          const json = JSON.parse(text);
          const enumValue = Mustache.render(json.key.trim(), input);

          return $t(`${json.path.trim()}.${enumValue}`);
        } catch (error) {
          Vue.$log.error(error);
        }

        return $t(Mustache.render(text.trim(), input));
      };
    },
    [TemplateFormatterEnum.DDMMYYYY]: function () {
      return (timestamp: string) => {
        const dateString = Mustache.render(timestamp, input).trim();

        return new Date(dateString).toLocaleDateString("de-de", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit"
        });
      };
    },
    [TemplateFormatterEnum.HHMM]: function () {
      return (timestamp: string) => {
        const dateString = Mustache.render(timestamp, input).trim();

        return `${new Date(dateString).getHours().toString().padStart(2, "0")}:${new Date(dateString)
          .getMinutes()
          .toString()
          .padStart(2, "0")}`;
      };
    },
    [TemplateFormatterEnum.BOOLEAN]: function () {
      return (value: boolean) => {
        const booleanString = Mustache.render(value, input).trim();
        if (booleanString === "true" || booleanString === "1") {
          return $t("yes");
        }
        return $t("no");
      };
    },
    [TemplateFormatterEnum.PERCENTAGES]: function () {
      return (value: number) => {
        const percentageString = Mustache.render(value, input).trim();
        try {
          const percentageValue = parseFloat(percentageString);
          if (isNaN(percentageValue)) {
            return `${percentageString} %`;
          }

          return `${percentageValue.toFixed(2).replace(".", ",")} %`;
        } catch (e) {
          return `${percentageString} %`;
        }
      };
    }
  });
}
